import { localize } from '@vscode-alt/monaco-editor/esm/vs/nls';
import { Queue } from '@vscode-alt/monaco-editor/esm/vs/base/common/async';
import * as json from '@vscode-alt/monaco-editor/esm/vs/base/common/json';
import { setProperty } from '@vscode-alt/monaco-editor/esm/vs/base/common/jsonEdit';
import { Edit } from '@vscode-alt/monaco-editor/esm/vs/base/common/jsonFormatter';
import { Disposable, IReference } from '@vscode-alt/monaco-editor/esm/vs/base/common/lifecycle';
import { isArray } from '@vscode-alt/monaco-editor/esm/vs/base/common/types';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { EditOperation } from '@vscode-alt/monaco-editor/esm/vs/editor/common/core/editOperation';
import { Range } from '@vscode-alt/monaco-editor/esm/vs/editor/common/core/range';
import { Selection } from '@vscode-alt/monaco-editor/esm/vs/editor/common/core/selection';
import { ITextModel } from '@vscode-alt/monaco-editor/esm/vs/editor/common/model';
import { IResolvedTextEditorModel } from '@vscode-alt/monaco-editor/esm/vs/editor/common/services/resolverService';
import { ContextKeyExpr } from '@vscode-alt/monaco-editor/esm/vs/platform/contextkey/common/contextkey';
import { IUserFriendlyKeybinding } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/keybinding';
import { ResolvedKeybindingItem } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/resolvedKeybindingItem';
import { StaticServices } from '@vscode-alt/monaco-editor/esm/vs/editor/standalone/browser/standaloneServices';

import { inject, injectable, postConstruct } from 'inversify';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import {
    ClientTypes, IClientConfigurationService, IEnvironmentService, IKeybindingEditingService,
    ILifecycleService, LifecyclePhase, IKeybindingService, CacheTypenames, IKeybindingSource, IKeybindingItem_input,
} from '@workbench-stack/core';
import { ClientTypes as FilesTypes, IClientFileService as IFileService, ITextFileService, ResolveFileContentDocument } from '@files-stack/core';
import { ClientTypes as CommonTypes } from '@common-stack/client-core';
import { KeybindingIO } from './keyboardMapper';
import { KeybindingItemFragmentDoc, KeybindingsFragmentDoc } from '@workbench-stack/components/lib/editors';

@injectable()
export class KeybindingsEditingService extends Disposable implements IKeybindingEditingService {

    public _serviceBrand: undefined;
    private queue: Queue<void>;

    private resource: URI;

    constructor(
        // @ITextModelService private readonly textModelResolverService: ITextModelService,
        @inject(CommonTypes.ApolloClient)
        public client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        public cache: InMemoryCache,
        @inject(ClientTypes.ILifecycleService)
        private lifecycleService: ILifecycleService,
        @inject(FilesTypes.ITextFileService)
        private textFileService: ITextFileService,
        @inject(FilesTypes.IFileService)
        private fileService: IFileService,
        @inject(ClientTypes.IConfigurationService)
        private configurationService: IClientConfigurationService,
        @inject(ClientTypes.IEnvironmentService)
        private environmentService: IEnvironmentService,
        @inject(ClientTypes.IKeybindingService)
        private keybindingService: IKeybindingService,
    ) {
        super();
        this.queue = new Queue<void>();
    }

    @postConstruct()
    private postSetup() {
        this.lifecycleService.when(LifecyclePhase.Restored).then(() => {
            this.resource = URI.file(this.environmentService.appKeybindingsPath);
        });
    }

    //@sri custom changes to get file content
    private async getContent(model) {
        try {
            let content = null;
            if (model) {
                content = model.getValue();
            } else {
                content = await this.textFileService.read(this.resource);
                content = content ? content.value : '';
            }
            return content;
        } catch (e) {
            console.error('>Error-', e);
        }
    }


    addKeybinding(keybindingItem: ResolvedKeybindingItem, key: string, when: string | undefined): Promise<void> {
        return this.queue.queue(() => this.doEditKeybinding(keybindingItem, key, when, true)); // queue up writes to prevent race conditions
    }

    editKeybinding(keybindingItem: ResolvedKeybindingItem, key: string, when: string | undefined): Promise<void> {
        return this.queue.queue(() => this.doEditKeybinding(keybindingItem, key, when, false)); // queue up writes to prevent race conditions
    }

    resetKeybinding(keybindingItem: ResolvedKeybindingItem): Promise<void> {
        return this.queue.queue(() => this.doResetKeybinding(keybindingItem)); // queue up writes to prevent race conditions
    }

    removeKeybinding(keybindingItem: ResolvedKeybindingItem): Promise<void> {
        return this.queue.queue(() => this.doRemoveKeybinding(keybindingItem)); // queue up writes to prevent race conditions
    }


    private async doEditKeybinding(keybindingItem: ResolvedKeybindingItem, key: string, when: string | undefined, add: boolean): Promise<void> {
        const reference = await this.resolveAndValidate();
        const model = reference.object.textEditorModel;
        if (add) {
            this.updateKeybinding(keybindingItem, key, when, model, -1);
        } else {
            const userKeybindingEntries = <IUserFriendlyKeybinding[]>json.parse(model.getValue());
            const userKeybindingEntryIndex = this.findUserKeybindingEntryIndex(keybindingItem, userKeybindingEntries);
            this.updateKeybinding(keybindingItem, key, when, model, userKeybindingEntryIndex);
            if (keybindingItem.isDefault && keybindingItem.resolvedKeybinding) {
                this.removeDefaultKeybinding(keybindingItem, model);
            }
        }
        try {
            await this.save();
        } finally {
            reference.dispose();
        }
    }

    private doRemoveKeybinding(keybindingItem: ResolvedKeybindingItem): Promise<void> {
        return this.resolveAndValidate()
            .then(reference => {
                const model = StaticServices.modelService.get().getModel(this.resource); //@sri 
                if (keybindingItem.isDefault) {
                    this.removeDefaultKeybinding(keybindingItem, model).then(() => {
                        return this.save().then(() => { });
                    });
                } else {
                    this.removeUserKeybinding(keybindingItem, model).then(() => {
                        return this.save().then(() => { });
                    });
                }
            });
    }

    private doResetKeybinding(keybindingItem: ResolvedKeybindingItem): Promise<void> {
        return this.resolveAndValidate()
            .then(reference => {
                const model = StaticServices.modelService.get().getModel(this.resource);
                if (!keybindingItem.isDefault) {
                    this.removeUnassignedDefaultKeybinding(keybindingItem, model).then(() => {
                        this.removeUserKeybinding(keybindingItem, model).then(() => {
                            return this.save().then(() => { });
                        });
                    });
                }
            });
    }

    private save(): Promise<any> {
        const model = StaticServices.modelService.get().getModel(this.resource);
        if (model) {
            return this.textFileService.write(this.resource, model.getValue());
        }
        return Promise.resolve(null);
    }

    //@sri changes to get options
    private getOptions(model) {
        let tabSize = 4;
        let insertSpaces = true;
        let eol;
        if (model) {
            tabSize = model.getOptions().tabSize;
            insertSpaces = model.getOptions().insertSpaces;
            eol = model.getEOL();
            return { tabSize, insertSpaces, eol };
        } else {
            eol = this.configurationService.getValue<{ eol: string }>('files', { overrideIdentifier: 'json' })['eol'];
        }
        return { tabSize, insertSpaces, eol };
    }

    private async updateKeybinding(keybindingItem: ResolvedKeybindingItem, newKey: string, when: string | undefined, model: ITextModel, userKeybindingEntryIndex: number): Promise<void> {
        const { tabSize, insertSpaces, eol } = this.getOptions(model);
        let content = await this.getContent(model);

        if (userKeybindingEntryIndex !== -1) {
            // Update the keybinding with new key
            await this.applyEditsToBuffer(setProperty(content, [userKeybindingEntryIndex, 'key'], newKey, {
                tabSize,
                insertSpaces,
                eol,
            })[0], model);
            content = await this.getContent(model);
            const edits = setProperty(content, [userKeybindingEntryIndex, 'when'], when, {
                tabSize,
                insertSpaces,
                eol,
            });
            if (edits.length > 0) {
                await this.applyEditsToBuffer(edits[0], model);
            }
        } else {
            // Add the new keybinding with new key
            await this.applyEditsToBuffer(setProperty(content, [-1], this.asObject(newKey, keybindingItem.command, when, false), {
                tabSize,
                insertSpaces,
                eol,
            })[0], model);
        }

        //@sri changes to update value in cache
        let keybindings = [];
        const { items } = this.client.readFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
        });
        let id = 0;
        let isAdd = false;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.command === keybindingItem.command) {
                when = when ? when : '-';
                if (item.source === IKeybindingSource.User && !keybindingItem.isDefault) {
                    keybindings.push({ ...item, when, id: `${CacheTypenames.KeybindingItem}:${id++}`, key: newKey, source: IKeybindingSource.User });
                    isAdd = true;
                } else if (item.source === IKeybindingSource.Default && !keybindingItem.isDefault) {
                    keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}` });
                } else if (item.source === IKeybindingSource.User && keybindingItem.isDefault) {
                    keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}`, when, key: newKey });
                    isAdd = true;
                } else if (item.source === IKeybindingSource.Default && keybindingItem.isDefault) {
                    if (!isAdd) {
                        keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}`, when, key: newKey, source: IKeybindingSource.User });
                        isAdd = true;
                    }
                    keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}` });
                }
            } else {
                keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}` });
            }
        }
        this.client.writeFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
            data: { items: keybindings, __typename: CacheTypenames.Keybindings },
        });
    }

    private async removeUserKeybinding(keybindingItem: ResolvedKeybindingItem, model: ITextModel): Promise<void> {
        const { tabSize, insertSpaces, eol } = this.getOptions(model);
        let content = await this.getContent(model);

        const userKeybindingEntries = <IUserFriendlyKeybinding[]>json.parse(content);
        const userKeybindingEntryIndex = this.findUserKeybindingEntryIndex(keybindingItem, userKeybindingEntries);
        if (userKeybindingEntryIndex !== -1) {
            await this.applyEditsToBuffer(setProperty(content, [userKeybindingEntryIndex], undefined, { tabSize, insertSpaces, eol })[0], model);
        }

        //@sri changes to remove default keybinding from the cache
        let keybindings = [];
        const { items } = this.client.readFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
        });
        let id = 0;
        items.forEach((item) => {
            const isEqualWhen = keybindingItem.when ? item.when === keybindingItem.when.serialize() : item.when === '-';
            if (!(item.command === keybindingItem.command && isEqualWhen && item.key === keybindingItem.keypressParts.join(' ') && item.source === IKeybindingSource.User)) {
                keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}` });
            }
        });
        this.client.writeFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
            data: { items: keybindings, __typename: CacheTypenames.Keybindings },
        });
    }

    private async removeDefaultKeybinding(keybindingItem: ResolvedKeybindingItem, model: ITextModel): Promise<void> {
        const key = keybindingItem.resolvedKeybinding ? keybindingItem.resolvedKeybinding.getUserSettingsLabel() : null;
        const { tabSize, insertSpaces, eol } = this.getOptions(model);
        let content = await this.getContent(model);

        if (key) {
            await this.applyEditsToBuffer(setProperty(content, [-1],
                this.asObject(key, keybindingItem.command, keybindingItem.when ? keybindingItem.when.serialize() : undefined, true), {
                tabSize,
                insertSpaces,
                eol,
            })[0], model);
        }

        //@sri changes to remove default keybinding from the cache
        let keybindings = [];
        const { items } = this.client.readFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
        });
        let id = 0;
        items.forEach((item) => {
            const isEqualWhen = keybindingItem.when ? item.when === keybindingItem.when.serialize() : item.when === '-';
            if (!(item.command === keybindingItem.command && isEqualWhen && item.key === keybindingItem.keypressParts.join(' ') && item.source === IKeybindingSource.Default)) {
                keybindings.push({ ...item, id: `${CacheTypenames.KeybindingItem}:${id++}` });
            }
        });
        this.client.writeFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
            data: { items: keybindings, __typename: CacheTypenames.Keybindings },
        });
    }

    private async removeUnassignedDefaultKeybinding(keybindingItem: ResolvedKeybindingItem, model: ITextModel): Promise<void> {
        const { tabSize, insertSpaces, eol } = this.getOptions(model);
        const content = await this.getContent(model);
        const userKeybindingEntries = <IUserFriendlyKeybinding[]>json.parse(content);
        const indices = this.findUnassignedDefaultKeybindingEntryIndex(keybindingItem, userKeybindingEntries).reverse();
        for (const index of indices) {
            await this.applyEditsToBuffer(setProperty(content, [index], undefined, { tabSize, insertSpaces, eol })[0], model);
        }

        //@sri changes
        const { items } = this.client.readFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
        });
        let keybindings = items.slice(0);
        const keybindingIndex = keybindings.findIndex((item) => {
            const isEqualWhen = keybindingItem.when ? item.when === keybindingItem.when.serialize() : item.when === '-';
            return (item.command === keybindingItem.command && item.key === keybindingItem.keypressParts.join(' ')
                && item.source === IKeybindingSource.User && isEqualWhen);
        });
        const defaultKeybindings = this.keybindingService.getDefaultKeybindings()
            .filter((keybinding) => (keybinding.command === keybindingItem.command)).reverse();

        defaultKeybindings.forEach((keybinding) => {
            const defItem = {
                command: keybinding.command,
                key: keybinding.keypressParts.join(' '),
                when: keybinding.when.serialize(),
                source: IKeybindingSource.Default,
                __typename: CacheTypenames.KeybindingItem,
            };
            const i = keybindings.findIndex((item) => item.command === defItem.command && item.key === defItem.key
                && item.when === defItem.when && item.source === IKeybindingSource.Default);
            if (i === -1) {
                keybindings.splice(keybindingIndex, 0, defItem);
            }
        });
        this.client.writeFragment({
            fragment: KeybindingsFragmentDoc,
            id: CacheTypenames.Keybindings,
            data: {
                items: keybindings.map((item, index) => ({ ...item, id: `${CacheTypenames.KeybindingItem}:${index}` })),
                __typename: CacheTypenames.Keybindings,
            },
        });
    }

    private findUserKeybindingEntryIndex(keybindingItem: ResolvedKeybindingItem, userKeybindingEntries: IUserFriendlyKeybinding[]): number {
        for (let index = 0; index < userKeybindingEntries.length; index++) {
            const keybinding = userKeybindingEntries[index];
            if (keybinding.command === keybindingItem.command) {
                if (!keybinding.when && !keybindingItem.when) {
                    return index;
                }
                if (keybinding.when && keybindingItem.when) {
                    const contextKeyExpr = ContextKeyExpr.deserialize(keybinding.when);
                    if (contextKeyExpr && contextKeyExpr.serialize() === keybindingItem.when.serialize()) {
                        return index;
                    }
                }
            }
        }
        return -1;
    }

    private findUnassignedDefaultKeybindingEntryIndex(keybindingItem: ResolvedKeybindingItem, userKeybindingEntries: IUserFriendlyKeybinding[]): number[] {
        const indices: number[] = [];
        for (let index = 0; index < userKeybindingEntries.length; index++) {
            if (userKeybindingEntries[index].command === `-${keybindingItem.command}`) {
                indices.push(index);
            }
        }
        return indices;
    }

    private asObject(key: string, command: string | null, when: string | undefined, negate: boolean): any {
        const object: any = { key };
        if (command) {
            object['command'] = negate ? `-${command}` : command;
        }
        if (when) {
            object['when'] = when;
        }
        return object;
    }


    private async applyEditsToBuffer(edit: Edit, model: ITextModel): Promise<void> {
        if (model) {
            const startPosition = model.getPositionAt(edit.offset);
            const endPosition = model.getPositionAt(edit.offset + edit.length);
            const range = new Range(startPosition.lineNumber, startPosition.column, endPosition.lineNumber, endPosition.column);
            let currentText = model.getValueInRange(range);
            const editOperation = currentText ? EditOperation.replace(range, edit.content) : EditOperation.insert(startPosition, edit.content);
            model.pushEditOperations([new Selection(startPosition.lineNumber, startPosition.column, startPosition.lineNumber, startPosition.column)], [editOperation], () => []);
        } else {
            const value = await this.getContent(model);
            const newValue = value.substr(0, edit.offset) + edit.content + value.substr(edit.offset + edit.length);
            await this.textFileService.write(this.resource, newValue);
        }
    }


    private resolveModelReference(): Promise<IReference<IResolvedTextEditorModel>> {
        return this.fileService.resolve(this.resource)
            .then(exists => {
                return Promise.resolve(null);
            }).catch((e) => {
                const EOL = this.configurationService.getValue<{ eol: string }>('files', { overrideIdentifier: 'json' })['eol'];
                return this.textFileService.write(this.resource, this.getEmptyContent(EOL), { encoding: 'utf8' });
            });
    }

    private resolveAndValidate(): Promise<IReference<IResolvedTextEditorModel>> {
        // Target cannot be dirty if not writing into buffer
        if (this.textFileService.isDirty(this.resource)) {
            return Promise.reject(new Error(localize('errorKeybindingsFileDirty', 'Unable to write because the keybindings configuration file is dirty. Please save it first and then try again.')));
        }

        return this.resolveModelReference()
            .then(reference => {
                //@sri custom changes to get editor model
                // const model = reference.object.textEditorModel;
                const model = StaticServices.modelService.get().getModel(this.resource);
                if (model) {
                    const EOL = model.getEOL();
                    if (model.getValue()) {
                        const parsed = this.parse(model);
                        if (parsed.parseErrors.length) {
                            return Promise.reject<any>(new Error(localize('parseErrors', 'Unable to write to the keybindings configuration file. Please open it to correct errors/warnings in the file and try again.')));
                        }
                        if (parsed.result) {
                            if (!isArray(parsed.result)) {
                                return Promise.reject<any>(new Error(localize('errorInvalidConfiguration', 'Unable to write to the keybindings configuration file. It has an object which is not of type Array. Please open the file to clean up and try again.')));
                            }
                        } else {
                            const content = EOL + [];
                            this.applyEditsToBuffer({ content, length: content.length, offset: model.getValue().length }, model).then(() => {
                                return reference;
                            });
                        }
                    } else {
                        const content = this.getEmptyContent(EOL);
                        this.applyEditsToBuffer({ content, length: content.length, offset: 0 }, model).then(() => {
                            return reference;
                        });
                    }
                }
                return reference;
            }).catch((e) => {
                console.error('error-', e);
            });
    }

    private parse(model: ITextModel): { result: IUserFriendlyKeybinding[], parseErrors: json.ParseError[] } {
        const parseErrors: json.ParseError[] = [];
        const result = json.parse(model.getValue(), parseErrors, { allowTrailingComma: true, allowEmptyContent: true } as any);
        return { result, parseErrors };
    }

    private getEmptyContent(EOL: string): string {
        return '// ' + localize('emptyKeybindingsHeader', 'Place your key bindings in this file to override the defaults') + EOL + '[]';
    }
}
